import React from 'react';
import { styled, Box, Typography, Breadcrumbs, Link as MUILink } from '@mui/material';
import { Link } from 'react-router-dom';
import Container from './Container';

export interface BreadcrumbItem {
  /** Link text */
  label: string;
  /** Navigation path */
  path?: string;
}

export interface PageProps {
  /** Page title */
  title: string;
  /** Page description */
  description?: string;
  /** Breadcrumb navigation items */
  breadcrumbs?: BreadcrumbItem[];
  /** Page content */
  children: React.ReactNode;
  /** Paper background for page */
  paperBackground?: boolean;
  /** Page actions - typically buttons displayed in the header */
  actions?: React.ReactNode;
}

const PageHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  color: theme.palette.text.primary,
}));

const PageDescription = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  maxWidth: '800px',
}));

const PageBreadcrumbs = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const PageContent = styled(Box)(({ theme }) => ({
  minHeight: '300px',
}));

const PageActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    '& > *': {
      width: '100%',
    },
  },
}));

const PaperBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  boxShadow: theme.shadows[1],
}));

export const Page: React.FC<PageProps> = ({
  title,
  description,
  breadcrumbs,
  children,
  paperBackground = false,
  actions,
}) => {
  const ContentWrapper = paperBackground ? PaperBox : React.Fragment;

  return (
    <Container>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <PageBreadcrumbs>
          <Breadcrumbs aria-label="breadcrumb">
            {breadcrumbs.map((item, index) => {
              const isLast = index === breadcrumbs.length - 1;
              
              return isLast || !item.path ? (
                <Typography key={item.label} color="text.primary">
                  {item.label}
                </Typography>
              ) : (
                <MUILink 
                  key={item.label} 
                  component={Link} 
                  to={item.path as string}
                  color="inherit"
                  underline="hover"
                >
                  {item.label}
                </MUILink>
              );
            })}
          </Breadcrumbs>
        </PageBreadcrumbs>
      )}
      
      <PageHeader>
        <PageTitle variant="h4">{title}</PageTitle>
        {description && (
          <PageDescription variant="body2">{description}</PageDescription>
        )}
      </PageHeader>
      
      {actions && <PageActions>{actions}</PageActions>}
      
      <PageContent>
        <ContentWrapper>{children}</ContentWrapper>
      </PageContent>
    </Container>
  );
};

export default Page;
